import { z } from 'zod';
import { currencyStepCents, isSupportedCurrency } from './currency.ts';
import { dateTimeFormat } from './schemas/formats.ts';

export const dustAssessmentSchema = z
  .object({
    lastDusted: z.enum(['within_three_months', 'over_three_months', 'unknown']),
    photoId: z.string().min(1).max(26).nullable(),
  })
  .strict();
export const dustPolicySchema = z.object({ upliftBasisPoints: z.number().int().min(1).max(10000).nullable() }).strict();
export type DustAssessment = z.infer<typeof dustAssessmentSchema>;
export type DustPolicy = z.infer<typeof dustPolicySchema>;

/** Evidence means a finalized upload verified by a company/location/link query. */
export function dustEvidenceDecision(
  assessment: DustAssessment,
  policy: DustPolicy | null,
  hasVerifiedLinkedPhoto: boolean,
) {
  assessment = dustAssessmentSchema.parse(assessment);
  policy = policy === null ? null : dustPolicySchema.parse(policy);
  if (assessment.lastDusted === 'unknown')
    return { blocked: true, upliftBasisPoints: 0, reason: 'dust_age_unknown' as const };
  if (assessment.lastDusted === 'within_three_months') return { blocked: false, upliftBasisPoints: 0, reason: null };
  if (assessment.photoId !== null && hasVerifiedLinkedPhoto)
    return { blocked: false, upliftBasisPoints: 0, reason: null };
  if (policy?.upliftBasisPoints == null)
    return { blocked: true, upliftBasisPoints: 0, reason: 'dust_photo_or_rate_required' as const };
  return { blocked: false, upliftBasisPoints: policy.upliftBasisPoints, reason: 'dust_photo_missing' as const };
}

/** Derived adjustment only. The caller adds it to the original subtotal once. */
export function dustUpliftCents(baseLaborCents: number, basisPoints: number, currencyCode: string): number {
  if (!isSupportedCurrency(currencyCode)) throw new RangeError('Choose a supported quote currency');
  const currencyStep = currencyStepCents(currencyCode);
  if (
    !Number.isSafeInteger(baseLaborCents) ||
    baseLaborCents < 0 ||
    baseLaborCents > 100000000 ||
    baseLaborCents % currencyStep !== 0 ||
    !Number.isSafeInteger(basisPoints) ||
    basisPoints < 0 ||
    basisPoints > 10000
  )
    throw new RangeError('Invalid dust pricing inputs');
  // Round once to the quote currency's quantum. Rounding to cents first could
  // turn 0.495 JPY into a whole yen instead of zero.
  const step = BigInt(currencyStep);
  const scale = 10000n * step;
  const amount = ((BigInt(baseLaborCents) * BigInt(basisPoints) + scale / 2n) / scale) * step;
  if (amount + BigInt(baseLaborCents) > 100000000n)
    throw new RangeError('Dust adjustment exceeds the supported amount');
  return Number(amount);
}

const quoteDustSchema = z
  .object({
    policy: dustPolicySchema.nullable(),
    assessment: dustAssessmentSchema.nullable(),
    contextSince: z.string().datetime(dateTimeFormat()),
    propertyId: z.string().nullable(),
    customerId: z.string(),
    evidenceVerified: z.boolean(),
    allocationBlocked: z.boolean().default(false),
    amountBlocked: z.boolean().default(false),
    upliftBasisPoints: z.number().int().min(0).max(10000),
    adjustmentCents: z.number().int().min(0).max(100000000),
    reason: z.enum(['dust_age_unknown', 'dust_photo_or_rate_required', 'dust_photo_missing']).nullable(),
  })
  .strict();
export type QuoteDust = z.infer<typeof quoteDustSchema>;
export type QuoteDustUploadContext = Pick<QuoteDust, 'propertyId' | 'customerId' | 'contextSince'>;
export function dustUploadContext(state: QuoteDust): QuoteDustUploadContext {
  return { propertyId: state.propertyId, customerId: state.customerId, contextSince: state.contextSince };
}
export function seedQuoteDust(
  policy: DustPolicy | null,
  quote: { property_id: string | null; customer_id: string; created_at: string },
): QuoteDust {
  return {
    policy,
    assessment: policy ? { lastDusted: 'unknown', photoId: null } : null,
    contextSince: quote.created_at,
    propertyId: quote.property_id,
    customerId: quote.customer_id,
    evidenceVerified: false,
    allocationBlocked: false,
    amountBlocked: false,
    upliftBasisPoints: 0,
    adjustmentCents: 0,
    reason: null,
  };
}
export function readQuoteDust(
  json: string | null | undefined,
  quote: { property_id: string | null; customer_id: string; created_at: string },
): QuoteDust {
  return json ? quoteDustSchema.parse(JSON.parse(json)) : seedQuoteDust(null, quote);
}
export function dustStatus(state: QuoteDust, verified: boolean, historical = false) {
  if (!state.assessment)
    return { blocked: false, evidenceChanged: false, evidenceVerified: false, upliftBasisPoints: 0, reason: null };
  const decision = dustEvidenceDecision(state.assessment, state.policy, historical ? state.evidenceVerified : verified);
  const evidenceChanged = !historical && decision.upliftBasisPoints !== state.upliftBasisPoints;
  return {
    ...decision,
    evidenceChanged,
    evidenceVerified: historical ? state.evidenceVerified : verified,
    blocked: decision.blocked || evidenceChanged || state.allocationBlocked || state.amountBlocked,
  };
}
